<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import {
    INativeDestinationSelection,
    TNativeDestination,
    useNativePrintingUi,
  } from '@/composition/printing/use-native-printing-ui';
  import { INativeWizardDevice } from '@/composition/printing/native-wizard-commit';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import V2NativeAssignmentPicker from '../V2NativeAssignmentPicker.vue';
  import { TNativeWizardType } from './NativeStepType.vue';

  export interface INativeWizardAssignResult {
    name: string;
    selection: INativeDestinationSelection[];
  }

  const props = withDefaults(
    defineProps<{
      device: INativeWizardDevice;
      type: TNativeWizardType;
      finishing: boolean;
      /**
       * Commit failure (e.g. late name collision) shown inline. Optional so
       * Vue 2 skips runtime type-checks when null is passed (a REQUIRED
       * `string | null` prop logs dev warnings on every null render).
       */
      error?: string | null;
    }>(),
    { error: null }
  );

  const emit = defineEmits<{
    (e: 'finish', result: INativeWizardAssignResult): void;
  }>();

  const ui = useNativePrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const name = ref(props.device.name);
  /** USB/BT names are the device-reported assignment keys — never renamed. */
  const nameEditable = props.device.isNew;

  const selection = ref<INativeDestinationSelection[]>(
    // ZPL printers exist to print labels: pre-check the only allowed chip.
    props.type === 'zpl' ? [{ destination: 'sticker' }] : []
  );

  const allowedDestinations = computed<TNativeDestination[]>(() =>
    props.type === 'zpl' ? ['sticker'] : ['ticket', 'comanda']
  );

  /**
   * Live validation against the FULL taken-name set (saved network +
   * discovered + persisted assignment names): matching an undiscovered
   * ghost's name would silently take over its assignments via
   * first-match-by-name re-resolution.
   */
  const nameError = computed<string | null>(() => {
    if (!nameEditable) return null;
    const trimmed = name.value.trim();
    if (!trimmed) return i18nT('printingV2.nativeNameRequired');
    if (ui.takenPrinterNames.value.has(trimmed)) {
      return i18nT('printingV2.nativeNameTaken');
    }
    return null;
  });

  const canFinish = computed(() => !nameError.value && !props.finishing);

  function finish() {
    if (!canFinish.value) return;
    emit('finish', {
      name: nameEditable ? name.value.trim() : props.device.name,
      selection: selection.value,
    });
  }
</script>

<template>
  <div class="native-step-assign">
    <div class="assign-field">
      <label class="assign-label">{{ $t('printingV2.assignName') }}</label>
      <template v-if="nameEditable">
        <PosUiInput
          :value="name"
          :maxlength="40"
          class="assign-name-input"
          @input="name = $event"
        />
        <div v-if="nameError" class="assign-name-error">{{ nameError }}</div>
      </template>
      <template v-else>
        <div class="assign-name-locked">
          <span class="assign-name-locked-value">{{ device.name }}</span>
          <span class="assign-name-locked-note">
            {{ $t('printingV2.nativeNameLockedNote') }}
          </span>
        </div>
      </template>
    </div>

    <div class="assign-field">
      <label class="assign-label">{{ $t('printingV2.assignQuestion') }}</label>
      <!--
        self-name: re-running the wizard on an EXISTING device must not warn
        "Replaces X" where X is the device itself (its current assignments
        are keyed to its own reported name).
      -->
      <V2NativeAssignmentPicker
        :value="selection"
        :allowed-destinations="allowedDestinations"
        :self-name="device.isNew ? null : device.name"
        :disabled="finishing"
        @input="selection = $event"
      />
      <div v-if="!selection.length" class="assign-none-hint">
        <PosUiIcon name="information-outline" class="assign-none-hint-icon" />
        {{ $t('printingV2.nativeAssignNoneHint') }}
      </div>
    </div>

    <div v-if="error" class="assign-error">
      <PosUiIcon name="alert" class="assign-error-icon" />
      {{ error }}
    </div>

    <div class="assign-footer">
      <PosUiButton
        color="primary"
        size="lg"
        icon="check"
        :loading="finishing"
        :disabled="!canFinish"
        @click="finish"
      >
        {{ $t('printingV2.finish') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .native-step-assign {
    @apply tw-flex tw-flex-col tw-gap-4;

    .assign-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;

      .assign-label {
        @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .assign-name-input {
        @apply tw-max-w-sm;
      }

      .assign-name-error {
        @apply tw-text-xs tw-text-red-600;
      }

      .assign-name-locked {
        @apply tw-flex tw-flex-col tw-gap-0.5;

        .assign-name-locked-value {
          @apply tw-text-sm tw-font-medium tw-text-neutral-800;
        }

        .assign-name-locked-note {
          @apply tw-text-xs tw-text-neutral-400;
        }
      }

      .assign-none-hint {
        @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-xs tw-text-neutral-500;

        .assign-none-hint-icon {
          @apply tw-flex-none tw-text-base;
        }
      }
    }

    .assign-error {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-700;

      .assign-error-icon {
        @apply tw-flex-none tw-text-base;
      }
    }

    .assign-footer {
      @apply tw-flex tw-justify-end tw-pt-2;
    }
  }
</style>
